import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useAuth, API, formatApiError } from "@/contexts/AuthContext";
import UpgradeLock from "@/components/dashboard/UpgradeLock";
import CanvaPanel from "@/components/dashboard/CanvaPanel";
import SocialPublishPanel from "@/components/dashboard/SocialPublishPanel";
import SocialScheduleList from "@/components/dashboard/SocialScheduleList";
import AdditionalSocialAccounts from "@/components/dashboard/AdditionalSocialAccounts";
import { planAtLeast, PLAN_BY_KEY } from "@/lib/planCatalog";
import {
  Facebook, Instagram, Youtube, Twitter, Music2,
  Image as ImageIcon, Video, Calendar, BarChart3, Users, Mail, Sparkles,
  Plus, Lock, Loader2, Copy as CopyIcon, Check, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

// Marketing & Content — connected social platforms.
// LinkedIn is intentionally omitted from the visible UI per the authoritative
// 2026-02 platform list (confirmed Facebook + Instagram OAuth, TikTok/X/YouTube
// marked honestly as coming soon). Any dormant backend LinkedIn plumbing is
// left alone — it just isn't surfaced here.
const PLATFORMS = [
  { id: "facebook", name: "Facebook", icon: Facebook, color: "#1877F2", tier: "Starter", status: "connect" },
  { id: "instagram", name: "Instagram", icon: Instagram, color: "#E4405F", tier: "Starter", status: "connect" },
  { id: "tiktok", name: "TikTok", icon: Music2, color: "#000000", tier: "Creator", status: "coming_soon" },
  { id: "x", name: "X", icon: Twitter, color: "#000000", tier: "Creator", status: "coming_soon" },
  { id: "youtube", name: "YouTube", icon: Youtube, color: "#FF0000", tier: "Creator", status: "coming_soon" },
];

const TABS = [
  { id: "compose", label: "Compose" },
  { id: "calendar", label: "Calendar" },
  { id: "photo", label: "Photo Studio" },
  { id: "video", label: "Video Studio" },
  { id: "canva", label: "Canva Studio" },
  { id: "campaigns", label: "Email Campaigns" },
  { id: "analytics", label: "Analytics" },
  { id: "clients", label: "Multi-client" },
];

export default function MarketingContent() {
  const { user } = useAuth();
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const activePlan = user?.workspace_context?.subscription_plan ||
    (ownWorkspace ? user?.subscription_plan : "Presale") || "Presale";
  const plan = activePlan.startsWith("Enterprise") ? "Enterprise" : activePlan;
  const [tab, setTab] = useState("compose");
  const [searchParams] = useSearchParams();
  const [connectedPages, setConnectedPages] = useState([]);
  const [connectionState, setConnectionState] = useState("loading");
  const [connecting, setConnecting] = useState(false);
  const connectPending = useRef(false);
  const workspaceKey = user?.active_workspace || user?.id;
  useEffect(() => {
    let cancelled = false;
    setConnectedPages([]);
    setConnectionState("loading");
    axios.get(`${API}/oauth/meta/status`).then(({ data }) => {
      if (cancelled) return;
      const pages = Array.isArray(data?.pages) ? data.pages : [];
      setConnectedPages(data?.mode === "live" ? pages.filter((p) => p.source === "live") : []);
      setConnectionState(data?.mode === "live" ? "ready" : "unavailable");
    }).catch(() => {
      if (!cancelled) setConnectionState("error");
    });
    return () => { cancelled = true; };
  }, [workspaceKey]);

  const connectMeta = async () => {
    if (connectPending.current) return;
    connectPending.current = true;
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/oauth/meta/start`);
      if (data?.mode !== "live" || !data?.authorize_url) {
        toast.info("Facebook and Instagram connection is not configured yet.");
        return;
      }
      const target = new URL(data.authorize_url);
      if (target.protocol !== "https:" || target.hostname !== "www.facebook.com") {
        throw new Error("Invalid connection destination");
      }
      window.location.assign(target.href);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "The account connection could not be started.");
    } finally {
      connectPending.current = false;
      setConnecting(false);
    }
  };


  useEffect(() => {
    if (searchParams.get("canva")) setTab("canva");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullAccess = !!((ownWorkspace && (user?.is_demo || user?.is_unlimited)) || activePlan.startsWith("Enterprise"));
  const canStarter = fullAccess || planAtLeast(plan, "Starter");
  const canCreator = fullAccess || planAtLeast(plan, "Creator");
  const canBusiness = fullAccess || planAtLeast(plan, "Business");
  const canAgency = fullAccess || planAtLeast(plan, "Agency");

  return (
    <div data-testid="marketing-content-page" className="max-w-6xl">
      <p className="zy-eyebrow mb-2 notranslate" translate="no">Marketing &amp; Content</p>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-[26px] sm:text-[28px] font-bold tracking-tight notranslate" translate="no">Marketing &amp; Content</h1>
          <p className="text-[14px] text-[#555] mt-1">
            Publish, schedule and design across every platform — powered by Zyntha.
          </p>
        </div>
        <div className="text-[12.5px] text-[#666]">
          Plan: <span className="font-semibold text-black notranslate" translate="no">{plan}</span>
        </div>
      </div>

      {/* Connected platforms */}
      <section className="mt-6 bg-white border border-[#eee] rounded-2xl p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4 gap-2">
          <h2 className="text-[15px] font-semibold">Connected accounts</h2>
          <span className="text-[12px] text-[#888]">
            Facebook Pages & Instagram business accounts
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const comingSoon = p.status === "coming_soon";
            const locked = !comingSoon && p.tier === "Creator" && !canCreator;
            const disabled = comingSoon || locked;
            const onConnect = () => { if (!disabled) connectMeta(); };
            return (
              <button
                key={p.id}
                disabled={disabled || connecting}
                onClick={onConnect}
                data-testid={`platform-${p.id}`}
                className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border transition-colors ${
                  disabled
                    ? "border-[#eee] bg-[#FAFAFB] cursor-not-allowed"
                    : "border-[#eee] hover:border-[#202B60] hover:bg-[#F4F6FB]"
                }`}
              >
                {locked && (
                  <span className="absolute top-1.5 right-1.5">
                    <Lock size={11} className="text-[#aaa]" />
                  </span>
                )}
                <Icon size={20} style={{ color: disabled ? "#bbb" : p.color }} />
                <span className={`text-[12px] font-medium ${disabled ? "text-[#888]" : "text-[#333]"}`}>
                  {p.name}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wider ${
                    comingSoon ? "text-[#8a6e1d]"
                    : locked ? "text-[#bbb]"
                    : "text-[#888]"
                  }`}
                  data-testid={`platform-${p.id}-status`}
                >
                  {comingSoon ? "Coming soon" : locked ? p.tier + "+" : connecting ? "Connecting…" : "Add account"}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-4 space-y-3">
          <p className="text-[13px] text-[#555]">
            Add your own Facebook Pages and linked Instagram business accounts through Meta.
            Choose which accounts to authorise in the connection window.
          </p>
          <button type="button" onClick={connectMeta} disabled={connecting}
            className="zy-btn-outline disabled:opacity-60" data-testid="mc-connect-account">
            {connecting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {connecting ? "Connecting…" : "Add Facebook / Instagram account"}
          </button>
          <div aria-live="polite" data-testid="mc-connected-accounts">
            {connectionState === "loading" && <p className="text-sm text-[#666]">Loading connected accounts…</p>}
            {connectionState === "error" && <p className="text-sm text-[#666]">Connected accounts could not be loaded. Refresh before publishing.</p>}
            {connectionState === "unavailable" && <p className="text-sm text-[#666]">Account connection is awaiting configuration.</p>}
            {connectionState === "ready" && connectedPages.length === 0 && <p className="text-sm text-[#666]">No accounts connected yet. Add your account above.</p>}
            {connectedPages.map((page) => (
              <div key={page.page_id} className="border border-[#eee] rounded-lg p-3 mt-2 text-sm">
                <p className="font-medium">Facebook: {page.page_name || "Connected Page"}</p>
                {page.ig_account_id && <p>Instagram: {page.ig_username ? `@${page.ig_username}` : "Linked business account"}</p>}
                {page.requires_reauth && <p className="text-amber-800">Reconnect this account to renew authorisation.</p>}
              </div>
            ))}
          </div>
        </div>
        {!canCreator && (
          <div className="mt-4">
            <UpgradeLock requiredPlan="Creator" feature="Additional social capabilities" compact />
          </div>
        )}
      </section>

      <AdditionalSocialAccounts key={workspaceKey} workspaceKey={workspaceKey} />

      {/* Tabs */}
      <nav className="mt-8 flex flex-wrap gap-1 border-b border-[#eee]">
        {TABS.map((t) => {
          const lock =
            (t.id === "photo" && !canStarter) ||
            false;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`mc-tab-${t.id}`}
              className={`px-3 sm:px-4 py-2.5 text-[13.5px] font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[#202B60] text-[#202B60]"
                  : "border-transparent text-[#666] hover:text-black"
              } ${lock ? "opacity-60" : ""}`}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className="mt-6">
        {tab === "compose" && <ComposePanel key={workspaceKey} canCreator={canCreator} />}
        {tab === "calendar" && (
          canCreator
            ? <SocialScheduleList key={workspaceKey} />
            : <UpgradeLock requiredPlan="Creator" feature="Content calendar & auto-scheduler" />
        )}
        {tab === "photo" && <PhotoPanel canCreator={canCreator} />}
        {tab === "video" && <VideoPanel canCreator={canCreator} />}
        {tab === "canva" && <CanvaPanel />}
        {tab === "campaigns" && (
          canBusiness
            ? <FeatureReady
                title="AI Email Campaigns"
                desc="Drag-and-drop builder, audience segmentation and AI-powered lead scoring."
                cta="Create your first campaign"
                tiles={["Drag-and-drop builder", "Audience segments", "AI subject lines", "Lead scoring"]}
              />
            : <UpgradeLock requiredPlan="Business" feature="AI Email Campaigns & Lead Scoring" />
        )}
        {tab === "analytics" && (
          canBusiness
            ? <FeatureReady
                title="Post Analytics"
                desc="Reach, likes, clicks and Zyona-suggested next actions per post."
                cta="View analytics dashboard"
                tiles={["Reach & impressions", "Engagement rate", "Click-through", "AI next actions"]}
              />
            : <UpgradeLock requiredPlan="Business" feature="Post Analytics" />
        )}
        {tab === "clients" && (
          canAgency
            ? <FeatureReady
                title="Multi-client Workspaces"
                desc="Manage social for multiple clients, with approval workflows and white-label reports."
                cta="Add your first client workspace"
                tiles={["Client switcher", "Approval flow", "White-label reports", "Per-client billing"]}
              />
            : <UpgradeLock requiredPlan="Agency" feature="Multi-client Social Management" />
        )}
      </div>
    </div>
  );
}

function ComposePanel({ canCreator }) {
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [showImageInput, setShowImageInput] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoExpiry, setPhotoExpiry] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const photoInput = useRef(null);
  const photoRequest = useRef(null);
  const photoPending = useRef(false);
  useEffect(() => () => photoRequest.current?.abort(), []);
  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || photoPending.current) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 8 * 1024 * 1024 || !file.size) {
      toast.error("Choose a JPEG, PNG or WebP photo up to 8 MB.");
      return;
    }
    photoPending.current = true;
    setPhotoUploading(true);
    const controller = new AbortController();
    photoRequest.current = controller;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/marketing/publish/images`, form, { signal: controller.signal });
      if (!data?.image_url) throw new Error();
      setImageUrl(data.image_url);
      setPhotoExpiry(data.expires_at);
      setPhotoPreview(URL.createObjectURL(file));
      toast.success("Photo uploaded. Review the post before publishing.");
    } catch (error) {
      if (!controller.signal.aborted) toast.error(formatApiError(error?.response?.data?.detail) || "The photo could not be uploaded. Your previous selection is unchanged.");
    } finally {
      photoPending.current = false;
      setPhotoUploading(false);
      photoRequest.current = null;
    }
  };
  const [hashtags, setHashtags] = useState([]);
  const [platform, setPlatform] = useState("instagram");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  // Phase 6A closure addendum: authoritative connected Meta pages.
  const [metaPages, setMetaPages] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/oauth/meta/status`);
        setMetaPages(data?.mode === "live" && Array.isArray(data?.pages)
          ? data.pages.filter((page) => page.source === "live" && !page.requires_reauth)
          : []);
      } catch { setMetaPages([]); }
    })();
  }, []);

  const generate = async () => {
    const idea = text.trim();
    if (!idea) {
      toast.error("Type a few words about your post first.");
      return;
    }
    if (idea.length < 3) {
      toast.error("Add a little more detail so Zyntha can craft a great caption.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/marketing/caption`, {
        idea,
        platform,
      });
      setText(data.caption || idea);
      setHashtags(data.hashtags || []);
      toast.success("Zyntha wrote your caption.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Zyntha couldn't generate a caption right now.");
    } finally {
      setGenerating(false);
    }
  };

  const copyAll = async () => {
    const tagLine = hashtags.length ? "\n\n" + hashtags.map((t) => `#${t}`).join(" ") : "";
    try {
      await navigator.clipboard.writeText(text + tagLine);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignored */ }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#eee] rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Sparkles size={15} style={{ color: "#202B60" }} />
          <h3 className="text-[14px] font-semibold">Create a post</h3>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#202B60] bg-[#EAF0FF] px-2 py-0.5 rounded-full">
            Zyntha caption AI · Gemini
          </span>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            data-testid="mc-platform-select"
            className="ml-auto text-[12.5px] border border-[#eee] rounded-md px-2 py-1.5 focus:outline-none focus:border-[#202B60]"
          >
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
            <option value="tiktok">TikTok</option>
            <option value="x">X</option>
            <option value="youtube">YouTube</option>
          </select>
        </div>

        <textarea
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What do you want to share today? A few words is enough — Zyntha will turn it into a caption."
          data-testid="mc-compose-textarea"
          className="w-full p-3 border border-[#eee] rounded-md text-[14px] focus:outline-none focus:border-[#202B60] resize-y"
        />

        {hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="mc-hashtags">
            {hashtags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center text-[12px] font-medium px-2 py-1 rounded-full"
                style={{ background: "#EAF0FF", color: "#202B60" }}
              >
                #{t}
              </span>
            ))}
            <button
              onClick={copyAll}
              data-testid="mc-copy-all"
              className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-medium text-[#555] hover:text-[#202B60] px-2 py-1 rounded-md border border-[#eee] bg-white"
            >
              {copied ? <Check size={11} /> : <CopyIcon size={11} />}
              {copied ? "Copied" : "Copy caption + tags"}
            </button>
          </div>
        )}

        <input type="file" accept="image/jpeg,image/png,image/webp" ref={photoInput}
          className="hidden" onChange={uploadPhoto} data-testid="mc-device-photo-input" />
        {(photoPreview || photoUploading) && (
          <div className="mt-3 border rounded-lg p-3 text-sm" aria-live="polite">
            {photoUploading && <p>Uploading photo…</p>}
            {photoPreview && <img src={photoPreview} alt="Selected social post photo" className="max-h-48 rounded-md" />}
            {photoExpiry && <p className="text-xs text-[#666] mt-2">
              Temporary image link expires {new Date(photoExpiry).toLocaleString()}.
              Meta can retrieve this photo through that link. Publishing still requires your confirmation.
            </p>}
            {photoPreview && <button type="button" disabled={photoUploading} className="underline mt-2"
              onClick={() => { setImageUrl(""); setPhotoPreview(""); setPhotoExpiry(null); }}>
              Remove photo from draft
            </button>}
          </div>
        )}
        {showImageInput && (
          <div id="mc-photo-link" className="mt-3 border border-[#eee] rounded-lg p-3">
            <label htmlFor="mc-photo-url" className="block text-sm font-medium mb-1">Photo link</label>
            <input id="mc-photo-url" type="url" value={imageUrl}
              onChange={(event) => { setImageUrl(event.target.value); setPhotoPreview(""); setPhotoExpiry(null); }}
              placeholder="https://…" className="w-full border rounded-md p-2 text-sm"
              data-testid="mc-photo-url" />
            <p className="text-xs text-[#666] mt-2">
              Use a publicly accessible HTTPS image link. This field does not upload a file from your device.
            </p>
            {imageUrl && <button type="button" className="text-sm underline mt-2"
              onClick={() => setImageUrl("")}>Remove photo link</button>}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            onClick={generate}
            disabled={generating}
            className="zy-btn-outline disabled:opacity-60"
            data-testid="mc-generate-caption"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {generating ? "Zyntha is writing…" : "Generate caption with Zyntha"}
          </button>
          <button type="button" className="zy-btn-outline" data-testid="mc-add-photo"
            disabled={photoUploading} onClick={() => photoInput.current?.click()}>
            {photoUploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
            {photoUploading ? "Uploading…" : "Add photo"}
          </button>
          <button type="button" className="zy-btn-outline" disabled={photoUploading}
            aria-expanded={showImageInput} aria-controls="mc-photo-link"
            onClick={() => setShowImageInput((current) => !current)}>
            Use image link
          </button>
          <button
            disabled={!canCreator}
            className={`zy-btn-outline ${!canCreator ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="mc-add-video"
          >
            <Video size={14} /> Add video
            {!canCreator && <Lock size={11} className="ml-1" />}
          </button>
          <SocialPublishPanel
            pages={metaPages}
            defaultMessage={text + (hashtags.length ? "\n\n" + hashtags.map((tag) => `#${String(tag).replace(/^#+/, "")}`).join(" ") : "")}
            defaultImageUrl={imageUrl.trim()}
            triggerDisabled={!canCreator || photoUploading}
            triggerLabel="Schedule"
            triggerVariant="outline"
            triggerTestId="mc-schedule"
            scheduleRequired
          />
          <div className="ml-auto">
            <SocialPublishPanel
              pages={metaPages}
              defaultMessage={text + (hashtags.length ? "\n\n" + hashtags.map((tag) => `#${String(tag).replace(/^#+/, "")}`).join(" ") : "")}
              defaultImageUrl={imageUrl.trim()}
              triggerDisabled={photoUploading}
              triggerLabel="Review & publish"
            />
          </div>
        </div>
      </div>
      {!canCreator && (
        <UpgradeLock
          requiredPlan="Creator"
          feature="Video uploads, scheduling & content calendar"
          compact
        />
      )}
    </div>
  );
}


function PhotoPanel({ canCreator }) {
  const basicTools = ["Resize", "Crop", "Filters", "Basic enhance"];
  const proTools = [
    "Background removal",
    "Object removal",
    "AI recolor",
    "AI enhance",
    "Sky replacement",
    "Skin retouch",
    "Smart filters",
    "Style transfer",
  ];
  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#eee] rounded-2xl p-5">
        <h3 className="text-[14px] font-semibold mb-3">Basic photo tools</h3>
        <div className="flex flex-wrap gap-2">
          {basicTools.map((t) => (
            <span
              key={t}
              className="px-3 py-1.5 text-[12.5px] font-medium rounded-full border border-[#eee] text-[#444] bg-white"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {canCreator ? (
        <div className="bg-white border border-[#eee] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={15} style={{ color: "#202B60" }} />
            <h3 className="text-[14px] font-semibold">AI Photo Suite</h3>
            <span className="ml-auto text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFF6D6] text-[#8a6e1d] font-bold">
              Coming soon
            </span>
          </div>
          <p className="text-[13px] text-[#555] mb-3">
            Nano Banana-powered image editing arrives in the next release — background removal,
            object removal, AI recolor, sky replacement and more.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {proTools.map((t) => (
              <div key={t} className="p-3 rounded-md bg-[#F4F6FB] text-[12.5px] font-medium text-center text-[#888]">
                {t}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <UpgradeLock requiredPlan="Creator" feature="Full AI Photo Suite (PicsArt-level)" />
      )}
    </div>
  );
}

function VideoPanel({ canCreator }) {
  const basic = ["Trim", "Captions", "Simple templates"];
  const pro = [
    "AI short-form video",
    "AI voiceovers",
    "B-roll auto-fill",
    "Auto-captions per language",
    "AI templates",
    "Style transfer",
    "Background blur",
    "Multi-clip editor",
  ];
  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#eee] rounded-2xl p-5">
        <h3 className="text-[14px] font-semibold mb-3">Basic video tools</h3>
        <div className="flex flex-wrap gap-2">
          {basic.map((t) => (
            <span
              key={t}
              className="px-3 py-1.5 text-[12.5px] font-medium rounded-full border border-[#eee] text-[#444] bg-white"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {canCreator ? (
        <div className="bg-white border border-[#eee] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={15} style={{ color: "#202B60" }} />
            <h3 className="text-[14px] font-semibold">AI Video Suite</h3>
            <span className="ml-auto text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFF6D6] text-[#8a6e1d] font-bold">
              Coming soon
            </span>
          </div>
          <p className="text-[13px] text-[#555] mb-3">
            Sora 2-powered AI video generation is arriving in an upcoming release — short-form video,
            AI voiceovers, auto-captions and more.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {pro.map((t) => (
              <div key={t} className="p-3 rounded-md bg-[#F4F6FB] text-[12.5px] font-medium text-center text-[#888]">
                {t}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <UpgradeLock requiredPlan="Creator" feature="Full AI Video Suite (CapCut-level)" />
      )}
    </div>
  );
}

// FeatureReady — the surfaces behind these panels (content calendar
// drag-and-drop, native email campaign builder, cross-platform analytics
// dashboard, multi-client workspace switcher) are described but not yet
// implemented end-to-end. Rather than show a clickable button that
// silently does nothing, we render a clear "In development" badge so
// the UI tells the truth. The feature description and the capability
// tiles are kept so the user can still see what's planned.
function FeatureReady({ title, desc, cta = "Open", tiles = [] }) {  // eslint-disable-line no-unused-vars
  return (
    <div className="bg-white border border-[#eee] rounded-2xl p-6 sm:p-8" data-testid="mc-feature-ready">
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0" style={{ background: "#EAF0FF" }}>
          <Sparkles size={18} style={{ color: "#202B60" }} />
        </span>
        <div className="flex-1">
          <p className="zy-eyebrow mb-1" style={{ color: "#202B60" }}>Planned for your workspace</p>
          <h3 className="text-[20px] font-bold tracking-tight">{title}</h3>
          <p className="text-[14px] text-[#555] mt-1 max-w-xl">{desc}</p>
        </div>
      </div>
      {tiles.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          {tiles.map((t) => (
            <div key={t} className="p-3 rounded-md bg-[#F4F6FB] text-[12.5px] font-medium text-center">
              {t}
            </div>
          ))}
        </div>
      )}
      <div className="mt-5 flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded-full"
          style={{ background: "#FFF4D6", color: "#8a6e1d" }}
          data-testid="mc-feature-status"
        >
          <Lock size={11} /> In development
        </span>
        <span className="text-[12px] text-[#888]">
          This surface is being built — we'll let you know when it's live.
        </span>
      </div>
    </div>
  );
}
